import React from 'react';
import Immutable from 'immutable';
import {
	styled,
	TextInput2,
	Icon,
	getSrchTokens,
	matchSrchTokens
} from 'ps-gui';
import memoizeOne from 'memoize-one';

const EMPTY_LIST = Immutable.List([]);
const EMPTY_SET = Immutable.Set([]);

function setTextSrch(node) {
	node = node.set('srch', getSrchTokens(node.get('text')).join(''));
	let children = node.get('children');
	if (children && children.size > 0) {
		children = children.map(c => setTextSrch(c));
		node = node.set('children', children);
	}
	return node;
}

const createTextSrch = memoizeOne(nodes => {
	return nodes.map(node => setTextSrch(node));
});

function filterRow(row, tokens) {
	if (matchSrchTokens(tokens, row.get('srch'))) {
		return row;
	}
	let children = row.get('children');
	if (children && children.size > 0) {
		children = filterRows(children, tokens);
		if (children.size === 0) {
			return null;
		}
		return row.set('children', children);
	} else {
		return null;
	}
}

function filterRows(rows, tokens) {
	return rows.map(row => filterRow(row, tokens)).filter(Boolean);
}

const filterItems = memoizeOne((filter, items) => {
	if (!items) {
		return items;
	}
	filter = (filter || '').trim();
	if (!filter) {
		return items;
	}
	const tokens = getSrchTokens(filter);
	items = filterRows(items, tokens);
	return items;
});

const getWithCheckedChild = memoizeOne((nodes, checkedNodes) => {
	let res = nodes.filter(node => {
		const children = node.get('children');
		if (!children || children.size === 0) {
			return false;
		}
		return children.some(child => checkedNodes.has(child.get('childId')));
	}).map(node => node.get('parentId'));
	return Immutable.Set(res);
});

export default class Bonsai extends React.PureComponent {

	constructor(props) {
		super(props);
		this.onFilterChange = this.onFilterChange.bind(this);
		const items = createTextSrch(props.items);
		this.state = {
			filter: '',
			items,
			filteredItems: items
		};

	}

	onFilterChange(value) {
		this.setState({
			filter: value,
			filteredItems: filterItems(value, this.state.items)
		});
	}

	static getDerivedStateFromProps(nextProps, prevState) {
		if (nextProps.data !== prevState.data) {
			const items = createTextSrch(nextProps.items);
			const filteredItems = filterItems(this.state.filter, items);
			return {
				items,
				filteredItems
			};
		}
		return null;
	}

	onChange(value) {
		if (typeof this.props.onChange === 'function') {
			if (value && value.size === 0) {
				value = null;
			}
			this.props.onChange(value);
		}
	}

	getParent(parentId) {
		return this.state.items.find(item => item.get('parentId') === parentId);
	}

	checkNode(checkedNodes, parentId, childId) {
		const parent = this.getParent(parentId);
		if (!parent) {
			return;
		}
		if (childId) {
			checkedNodes = checkedNodes.add(childId);
			const children = parent.get('children');
			if (children && children.size > 0) {
				if (!children.some(child => !checkedNodes.has(child.get('childId')))) {
					checkedNodes = checkedNodes.add(parentId);
					children.forEach(child => {
						checkedNodes = checkedNodes.delete(child.get('childId'));
					});
				}
			}
		} else {
			checkedNodes = checkedNodes.add(parentId);
		}
		this.onChange(checkedNodes);
	}

	uncheckNode(checkedNodes, parentId, childId) {
		const parent = this.getParent(parentId);
		if (!parent) {
			return;
		}
		if (childId) {
			checkedNodes = checkedNodes.delete(childId);
			if (checkedNodes.has(parentId)) {
				checkedNodes = checkedNodes.delete(parentId);
				const children = parent.get('children');
				if (children && children.size > 0) {
					children.forEach(child => {
						const cid = child.get('childId');
						if (cid !== childId) {
							checkedNodes = checkedNodes.add(cid);
						}
					});
				}
			}
		} else {
			checkedNodes = checkedNodes.delete(parentId);
			const children = parent.get('children');
			if (children && children.size > 0) {
				children.forEach(child => {
					checkedNodes = checkedNodes.delete(child.get('childId'));
				});
			}
		}
		this.onChange(checkedNodes);
	}

	renderNode(node, key, checkedNodes, withCheckedChild) {
		const children = node.get('children');
		const parentId = node.get('parentId');
		const childId = node.get('childId');
		const nodeId = childId ?? parentId;
		return (
			<Node key={key}>
				<NodeHeader
					onMouseDown={e => {
						if (checkedNodes.has(parentId) || checkedNodes.has(childId) || withCheckedChild.has(nodeId)) {
							this.uncheckNode(checkedNodes, parentId, childId);
						} else {
							this.checkNode(checkedNodes, parentId, childId);
						}
					}}
				>
					<Icon
						name={checkedNodes.has(parentId) || checkedNodes.has(childId) ? 'checkBoxChecked' : withCheckedChild.has(nodeId) ? 'checkBoxSemichecked' : 'checkBoxUnchecked'}
					/>
					<span className="text">{node.get('text')}</span>
				</NodeHeader>
				{
					children && children.size > 0 && 
						<Children>
							{
								children.map((child, childIndex) => this.renderNode(child, childIndex, checkedNodes, withCheckedChild))
							}
						</Children>
					
				}
			</Node>
		);

	}

	render() {
		const items = this.state.filteredItems || EMPTY_LIST;
		const checkedNodes = this.props.value || EMPTY_SET;
		const withCheckedChild = getWithCheckedChild(this.state.items, checkedNodes);
		return (
			<Wrapper maxHeight={this.props.maxHeight} width={this.props.width} minWidth={this.props.minWidth}>
				<TextInput2
					value={this.state.filter}
					onChange={this.onFilterChange}
					withClearButton
					rightIcon="search"
				/>
				<NodeContainer>
					{
						items.map((node, index) => this.renderNode(node, index, checkedNodes, withCheckedChild))
					}
				</NodeContainer>
			</Wrapper>
		);
	}
}

const Wrapper = styled.div`
	/*padding: 16px;*/
	display: flex;
	flex-flow: column;
	min-width: ${props => props.minWidth};
	max-height: ${props => props.maxHeight};
	width: ${props => props.width};
`;

const NodeContainer = styled.div`
	margin-top: 16px;
	overflow: auto;
	flex-grow: 1;
`;

const NodeHeader = styled.div`
	margin-bottom: 10px;
	cursor: pointer;
	&:hover {
		text-decoration: underline;
	}
	.icon {
		margin-right: 8px;
		vertical-align: middle;
	}
	.text {
		vertical-align: middle;
		font-weight: 400;
	}
`;

const Node = styled.div`
`;

const Children = styled.div`
	margin-left: 24px;
`;
